/**
 * Pure helpers for dual-track audio playback. Extracted from
 * `audio-bar.tsx` so the sync math is testable without a DOM.
 *
 * Design: given two tracks with independent offsets-from-startedAt,
 * pick whichever has the smaller offset as the "leader" (its sample at
 * t=0 is closest to `startedAt`). The follower's `currentTime` is
 * always `max(0, leaderAbsoluteMs - followerOffsetMs) / 1000`.
 *
 * Ties go to the agent (typical production shape: agent opens, caller
 * joins slightly later).
 */

import type { InteractionMessageResponse } from '@vagusx/clients';
import type { ConversationAudioDualTrack } from './types';

export interface LeaderRoles {
  leaderSrc: string;
  followerSrc: string;
  leaderOffsetMs: number;
  followerOffsetMs: number;
}

export function pickLeader(audio: ConversationAudioDualTrack): LeaderRoles {
  const agentOffset = audio.agentOffsetMs ?? 0;
  const callerOffset = audio.callerOffsetMs ?? 0;
  if (agentOffset <= callerOffset) {
    return {
      leaderSrc: audio.agentSrc,
      followerSrc: audio.callerSrc,
      leaderOffsetMs: agentOffset,
      followerOffsetMs: callerOffset,
    };
  }
  return {
    leaderSrc: audio.callerSrc,
    followerSrc: audio.agentSrc,
    leaderOffsetMs: callerOffset,
    followerOffsetMs: agentOffset,
  };
}

/**
 * Compute the target `currentTime` for a track given the absolute
 * position (relative to `startedAt`) and the track's own offset.
 * Clamped to >= 0 (the track has no audio before its first sample).
 */
export function trackTargetSeconds(absoluteMs: number, trackOffsetMs: number): number {
  return Math.max(0, (absoluteMs - trackOffsetMs) / 1000);
}

/**
 * Offset of a message from the recording's first sample, in ms.
 *
 * MAY BE NEGATIVE. The recorder comes up a beat AFTER the interaction row is
 * created, so a message written in that window genuinely predates the audio.
 * Callers that need a seek target clamp to 0 (`trackTargetSeconds` already
 * does); callers deciding which line is CURRENT must not clamp, or every
 * pre-recording message ties at 0 and the wrong one wins the highlight.
 *
 * Returns null when either timestamp is unparseable or no anchor is known.
 */
export function messageOffsetMs(messageTs: string, anchorIso: string | undefined): number | null {
  if (!anchorIso) return null;
  const anchor = new Date(anchorIso).getTime();
  const at = new Date(messageTs).getTime();
  if (!Number.isFinite(anchor) || !Number.isFinite(at)) return null;
  return at - anchor;
}

/**
 * Spoken lines with the wall-clock instant each utterance ENDED, SORTED BY
 * THAT INSTANT.
 *
 * The message array arrives ordered by server-assigned `sequence`, which is
 * not the same as timestamp order: transcript rows are written
 * fire-and-forget, so a write that lands late gets a higher sequence but an
 * earlier timestamp — the race `pairToolCalls` already documents for
 * `call_ended`. Every consumer below scans for the first line past a position
 * or measures the interval between neighbours, so a single out-of-order row
 * would light a line already spoken and mis-measure two gaps at once.
 * Sorting here makes all of them robust regardless of arrival order.
 *
 * Ties keep their original relative order (`sort` is stable), so lines
 * sharing a timestamp stay in the order the reader sees them.
 *
 * Absolute rather than anchor-relative: the INTERVAL between two ends is the
 * same number with or without an anchor, so `silenceGaps` can work on a call
 * that has no recording at all.
 */
function spokenLineEnds(
  messages: InteractionMessageResponse[]
): { id: string; endsAt: number; timestamp: string }[] {
  const lines: { id: string; endsAt: number; timestamp: string }[] = [];
  for (const m of messages) {
    if (m.kind !== 'user_message' && m.kind !== 'agent_message') continue;
    const endsAt = Date.parse(m.timestamp);
    if (!Number.isFinite(endsAt)) continue;
    lines.push({ id: m.id, endsAt, timestamp: m.timestamp });
  }
  return lines.sort((a, b) => a.endsAt - b.endsAt);
}

/** `spokenLineEnds` measured from the recording's first sample. */
function spokenLinesByOffset(
  messages: InteractionMessageResponse[],
  anchorIso: string
): { id: string; endsAt: number }[] {
  const anchor = new Date(anchorIso).getTime();
  if (!Number.isFinite(anchor)) return [];
  return spokenLineEnds(messages).map((l) => ({ id: l.id, endsAt: l.endsAt - anchor }));
}

/**
 * Which transcript line is being spoken at `currentMs`.
 *
 * A MESSAGE TIMESTAMP MARKS THE END OF ITS UTTERANCE, NOT THE START. That is
 * the whole subtlety here, and getting it backwards is a visible bug: a line
 * would light only once it had finished being spoken, running late by the
 * length of the utterance — several seconds on a long agent line, and not a
 * constant, so it reads as drift rather than as an offset.
 *
 * The evidence is the intervals themselves. On int `28d8834f`, anchored on the
 * recording's real first sample, consecutive timestamps sit 5.4s apart across
 * "Hi, this is Iris calling from Apex Medical. May I speak with Thomas?" —
 * which is how long that sentence takes to say, measured from the end of the
 * line before it. Both ends of the call corroborate it: the caller's opening
 * "Hello?" lands at 1.5s and the closing line at 209.3s of a 210s file, each
 * of which only makes sense as the moment the utterance FINISHED.
 *
 * So a line owns the span from the previous line's end up to its own end, and
 * the current line is the first whose end is still ahead of the playhead.
 * Exactly one is lit, always, and it is lit WHILE it is being spoken.
 *
 * Only spoken lines are candidates: tool calls and handoffs carry timestamps
 * but no audio, and letting one take the highlight would blank the line
 * actually being heard.
 *
 * DELIBERATELY UNCHANGED BY THE GAP WORK, and it is the weakest part of this
 * file. A line lights from the previous line's end, so across the 13.5s wait on
 * int `c1edd217` the "Take your time" bubble lights 13.5s before it is spoken.
 * The span `[previousEnd, ownEnd]` is a superset of the real utterance — the
 * highlight is early, never absent, and never on the wrong line — and tightening
 * it needs a duration nothing records. The alternatives are both worse: guessing
 * the start from character count is the mistake `recording-anchor.ts` deleted a
 * whole anchor source for, and going dark for the middle of an interval would
 * lose the follow-along on every ordinary turn to fix a rare one. The gutter and
 * `SilenceGapRow` now state the truth next to it, so an early highlight is
 * legible as an early highlight rather than read as fact.
 *
 * Returns null when there is no anchor to measure against — a call with no
 * usable `recordingStartedAtMs` highlights nothing rather than the wrong
 * thing. Past the final line the last one stays lit; what follows is the
 * hang-up, not another speaker.
 */
export function activeMessageIdAt(
  messages: InteractionMessageResponse[],
  anchorIso: string | undefined,
  currentMs: number | undefined
): string | null {
  if (anchorIso === undefined || currentMs === undefined) return null;
  const lines = spokenLinesByOffset(messages, anchorIso);
  // The first line whose END is at or after the playhead is the one being
  // spoken right now. `>=` rather than `>` so the instant a line ends still
  // belongs to it rather than flickering to the next.
  const current = lines.find((l) => l.endsAt >= currentMs);
  // Playhead is past every line: hold the last rather than going dark.
  return current?.id ?? lines[lines.length - 1]?.id ?? null;
}

/**
 * Where clicking a spoken line should START PLAYBACK, keyed by message id.
 *
 * A SEEK TARGET, NOT A CLAIM ABOUT WHERE THE LINE BEGINS. Nothing in the data
 * says where an utterance began: `interaction_messages` carries no duration,
 * and a timestamp marks the END of its own utterance (see `activeMessageIdAt`).
 * What we do know exactly is the previous line's end, and the line cannot have
 * begun before that — so this is the latest instant guaranteed to be at or
 * before the true start, which is the property a seek target needs. Seeking to
 * the raw timestamp instead lands at the END of the line the reader clicked and
 * they hear the next speaker.
 *
 * The cost is honest and bounded: across a long silence the click lands at the
 * top of the silence rather than at the words, so playback opens with dead air.
 * That is the right failure — the alternative is backing off from the line's
 * end by a guessed duration, which would land INSIDE a long line and silently
 * cut its opening words.
 *
 * DISCLOSURE IS PARTIAL, and worth stating rather than overselling. Above
 * `SILENCE_GAP_THRESHOLD_MS` a `SilenceGapRow` sits visibly above the bubble,
 * but only in gutter mode. The bubble's `title` names the seek instant on every
 * surface — and a `title` is a desktop-hover affordance: it never appears on
 * touch and is not surfaced by tabbing to the button. So a reader on a phone,
 * or on a wait under the threshold, can still meet a few seconds of silence
 * with nothing on screen explaining it. That is a smaller surprise than a
 * truncated line, which is why the trade is still the right one.
 *
 * Deliberately NOT what the gutter shows. The gutter states each line's own
 * exact timestamp; this is a derived, deliberately-early playback entry point.
 * They were the same number once, and that is precisely how a gapless
 * conversation ended up on screen.
 *
 * Clamped to >= 0: the opening line has nothing before it, and anything
 * computed before the first sample has no audio to seek to.
 *
 * Returns an empty map when there is no anchor, which leaves every bubble
 * un-clickable rather than seeking somewhere invented.
 */
export function lineSeekOffsets(
  messages: InteractionMessageResponse[],
  anchorIso: string | undefined
): Map<string, number> {
  const seeks = new Map<string, number>();
  if (anchorIso === undefined) return seeks;
  let previousEnd = 0;
  for (const line of spokenLinesByOffset(messages, anchorIso)) {
    seeks.set(line.id, Math.max(0, previousEnd));
    previousEnd = line.endsAt;
  }
  return seeks;
}

/**
 * How long the interval between two consecutive lines has to be before the
 * transcript calls it out.
 *
 * DERIVED FROM THE CORPUS, NOT FROM ONE CALL. The interval is
 * `silence + duration(closing line)`, so a threshold has to clear a plausible
 * utterance on its own — and "plausible" is a claim about production traffic,
 * not about whichever call prompted the feature. Measured over every
 * consecutive spoken-line interval in the last 30 days (n = 5,919 across 359
 * calls): p50 4.47s, p90 11.13s, p95 13.90s, p97 16.36s, p99 22.85s.
 *
 * 15s sits around p96. What that buys, at each candidate:
 *
 *   |  t  | % of turns | calls marked | markers/call (med, p90, max) |
 *   |-----|-----------|--------------|------------------------------|
 *   |  8s |     19.4% |          84% |            2, 9, 18          |
 *   | 12s |      7.8% |          67% |            1, 4, 7           |
 *   | 15s |      4.0% |          50% |            1, 2, 5           |
 *   | 20s |      1.3% |          20% |            1, 1, 3           |
 *
 * The first cut of this constant was 8s, justified by the four ordinary turns
 * on int `c1edd217` sitting at 4.3–4.5s. Those match the corpus p50 exactly and
 * say nothing about its tail: at 8s the median call grows two markers and the
 * p90 call grows nine, on a surface where a marker is supposed to read as a
 * finding. That is the "technically true and practically noise" outcome this
 * threshold exists to avoid, and n=4 from one call could not have caught it.
 *
 * WHAT 15s COSTS, stated plainly: the 8.3s hold on int `c1edd217` (a 10.5s
 * interval) gets no marker, because 10.5s is p89 of all intervals and is not
 * distinguishable from ordinary turn-taking by the only number available. The
 * gutter still shows it — 0:19 above 0:29 — which is the truthful part of the
 * fix and does not depend on a threshold at all. The marker is the narrower
 * claim: this wait is conspicuous even against a call centre's normal rhythm.
 *
 * A per-speaker threshold was considered and rejected: agent-closed intervals
 * really are longer (p95 14.95s vs 11.34s, because agent lines are longer), but
 * splitting the constant adds a knob to normalise a second-order effect, and
 * the motivating dead air is itself agent-closed — so the split cannot be
 * justified by the case that prompted it.
 */
export const SILENCE_GAP_THRESHOLD_MS = 15_000;

/**
 * A conspicuous wait between two consecutive spoken lines. Held in a map keyed
 * by the id of the line the wait FOLLOWS, so "the keyed line" below is the one
 * that ends just before the silence.
 */
export interface SilenceGap {
  /**
   * Interval from the keyed line's end to the NEXT line's end, in ms.
   *
   * AN EXACT UPPER BOUND ON THE SILENCE, not the silence itself: the tail of
   * this interval is the next line being spoken. It is exact in the sense that
   * matters — both endpoints are measured, nothing is estimated — and it is
   * the number that made the dead air on int `c1edd217` obvious.
   *
   * The bound is loosest when the closing line is long, which in practice means
   * an agent line: 76% of markers close on one. `SILENCE_GAP_THRESHOLD_MS`
   * carries the analysis.
   */
  intervalMs: number;
  /**
   * When the wait BEGINS — the keyed line's own timestamp, verbatim.
   *
   * An ISO instant rather than an offset, so the marker's gutter cell goes
   * through `gutterTimecode` exactly like every other row: a position in the
   * recording when the call is anchored, a wall clock when it is not. The
   * first cut returned an anchored offset and `null` without an anchor, and the
   * marker was then the one row in the column that could not render its cell —
   * on 143 real interactions in 30 days. Formatting belongs to the row.
   */
  startsAtIso: string;
}

/**
 * Conspicuous waits in the conversation, keyed by the id of the line each one
 * FOLLOWS.
 *
 * Keyed by the OPENING line, not the closing one, because that is where the
 * marker has to be drawn. Keying it on the closing line and drawing it just
 * above put the marker below any tool row that fired during the wait, and the
 * column then ran 1:09 → 1:19 → 1:09 → 1:19 — the same non-monotonic column the
 * chained labels produced, reintroduced by layout instead of by arithmetic.
 * Anchored to the line it follows, the marker lands before everything that
 * happened during the wait, and a tool call inside the silence reads as what it
 * is. Computed for the whole transcript at once for the same reason
 * `lineSeekOffsets` is: a row cannot see its neighbours.
 *
 * Needs no anchor: an interval is the difference between two timestamps, so a
 * call with no recording shows its waits too.
 *
 * No marker before the FIRST line. The interval from the recording's first
 * sample to the opening line is mostly ring time on an outbound call, which is
 * not a wait in the conversation and would put a marker at the top of nearly
 * every transcript.
 */
export function silenceGaps(
  messages: InteractionMessageResponse[],
  thresholdMs: number = SILENCE_GAP_THRESHOLD_MS
): Map<string, SilenceGap> {
  const gaps = new Map<string, SilenceGap>();
  const lines = spokenLineEnds(messages);
  for (let i = 1; i < lines.length; i++) {
    const previous = lines[i - 1]!;
    const line = lines[i]!;
    const intervalMs = line.endsAt - previous.endsAt;
    if (intervalMs < thresholdMs) continue;
    gaps.set(previous.id, { intervalMs, startsAtIso: previous.timestamp });
  }
  return gaps;
}

/**
 * The silent head of an outbound recording: egress started, phone still ringing.
 *
 * COMPLETES A THOUGHT `silenceGaps` DELIBERATELY LEFT. Its doc says why it
 * draws no marker before the first line — "the interval from the recording's
 * first sample to the opening line is mostly ring time on an outbound call,
 * which is not a wait in the conversation". True, and the reason a marker keyed
 * to a LINE is the wrong instrument: the interval is not between two turns, it
 * is between the file starting and the call starting. So it gets its own row,
 * measured between its own two instants, and `silenceGaps` keeps its rule.
 *
 * WHY THERE IS ANYTHING TO SHOW. No carrier early media reaches the room, so
 * the ring records as silence: measured across prod calls with rings of 4.06s,
 * 8.00s and 22.95s, the pre-pickup window sits at the office-ambience floor
 * (~-64 dBFS) against -13 dBFS for real ringback. A reviewer presses play,
 * hears nothing, and reports a broken agent. On int `a61b4bf1` the greeting was
 * on the wire 2.90s after the pickup — the dead air was 8.00s of ring in front
 * of it. See docs/ring-lead-in.md (ADR-339).
 */
export const RING_LEAD_IN_MIN_MS = 1_000;

/** The pre-pickup head of a recording, when there is one worth naming. */
export interface RingLeadIn {
  /**
   * The pickup's offset into the recording, in ms.
   *
   * EXACT, with no "up to" hedge — unlike {@link SilenceGap.intervalMs}, whose
   * tail is a line being spoken. Both endpoints here are measured instants that
   * bound nothing but silence, so the number IS the lead-in.
   *
   * NOT dial-to-pickup. The runtime also knows that (`tRingMs`), and it is
   * about a second longer because egress starts after the dial — on
   * `a61b4bf1`, 9128ms against this 8001ms. Labelling the row with one and
   * shading the scrubber with the other would reintroduce exactly the
   * two-numbers-one-derived confusion `gutterTimecode` documents.
   */
  durationMs: number;
}

/**
 * Resolve the recording's silent pre-pickup head, or null when there is none
 * to show.
 *
 * NULL MEANS NULL, the rule `recording-anchor.ts` exists to enforce: a shaded
 * region in the wrong place is worse than no shaded region, because the reader
 * cannot tell which they are looking at. Every rejection below is a real shape
 * seen in prod, not a defensive guess:
 *
 *   - **No pickup instant.** Inbound calls and browser tests never observe a
 *     SIP answer, a call nobody picked up has none, and no row predating the
 *     column has one. This is the common case, by far.
 *   - **No anchor.** Same contract as the transcript highlight: without
 *     `recordingStartedAtMs` there is no position in the file to mark.
 *   - **Pickup at or before the anchor.** 2 of 53 prod calls — the far end was
 *     already active when the agent joined (`dial.agentLate`), so egress
 *     started after the pickup. A negative lead-in would shade backwards.
 *   - **Under {@link RING_LEAD_IN_MIN_MS}.** 6 of 53, from 296ms to 976ms. A
 *     sub-second head confuses nobody and a row on almost every call is noise
 *     — the same judgement `SILENCE_GAP_THRESHOLD_MS` makes.
 *   - **Longer than the recording.** Not seen in prod, but it is the shape a
 *     retry leaves transiently: the interaction row is reused, and the pickup
 *     and the recording anchor are written by different calls, so between the
 *     two writes the stored pair can straddle attempts.
 */
export function ringLeadIn(
  anchorIso: string | undefined,
  sipAnsweredIso: string | null | undefined,
  opts: { durationMs?: number | undefined } = {}
): RingLeadIn | null {
  if (!anchorIso || !sipAnsweredIso) return null;
  const anchor = new Date(anchorIso).getTime();
  const pickup = new Date(sipAnsweredIso).getTime();
  if (!Number.isFinite(anchor) || !Number.isFinite(pickup)) return null;
  const durationMs = pickup - anchor;
  if (durationMs < RING_LEAD_IN_MIN_MS) return null;
  if (opts.durationMs !== undefined && durationMs >= opts.durationMs) return null;
  return { durationMs };
}

/**
 * The lead-in as a percentage of the recording, for the scrubber's shade.
 *
 * Returns null when there is nothing to size it against, so the caller renders
 * no shade rather than a zero-width one. Clamped because a stored pair that
 * straddles a retry (see `ringLeadIn`) must not produce a bar wider than its
 * track.
 */
export function leadInWidthPercent(leadInMs: number, durationMs: number | null): number | null {
  if (durationMs === null || !Number.isFinite(durationMs) || durationMs <= 0) return null;
  if (!Number.isFinite(leadInMs) || leadInMs <= 0) return null;
  return Math.min(100, (leadInMs / durationMs) * 100);
}
